// VerticalNav.jsx
// Right-edge vertical navigation sidebar with colored text on neutral backgrounds

import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorldState } from './state/WorldStateContext.jsx';
import { EOS_REQUIREMENTS } from '../systems/EOSSystem.js';
import { FuelGauge } from './fuel/FuelGauge.jsx';
import { WINGS_CONFIG, getWingsState } from '../systems/WingsSystem.js';

// Calculate tier from rep score
function calculateTier(score) {
  if (score >= 15000) return 5;
  if (score >= 7500) return 4;
  if (score >= 3000) return 3;
  if (score >= 1000) return 2;
  return 1;
}

const NAV_ITEMS = [
  { id: 'ops', labelKey: 'nav.opsCenter', subtitleKey: 'nav.opsCenterSub', route: '/OpsCenter', color: '#60ffd0' },
  { id: 'map', labelKey: 'nav.worldMap', subtitleKey: 'nav.worldMapSub', route: '/WorldMap', color: '#3b82f6' },
  { id: 'routes', labelKey: 'nav.routes', subtitleKey: 'nav.routesSub', route: '/routes', color: '#06b6d4', simpleOnly: true },
  { id: 'market', labelKey: 'nav.aircraft', subtitleKey: 'nav.aircraftSub', route: '/AircraftMarket', color: '#f59e0b' },
  { id: 'fleet', labelKey: 'nav.myFleet', subtitleKey: 'nav.myFleetSub', route: '/fleet', color: '#14b8a6' },
  { id: 'financials', labelKey: 'nav.finances', subtitleKey: 'nav.financesSub', route: '/financials', color: '#22c55e' },
  { id: 'hubs', labelKey: 'nav.hubStore', subtitleKey: 'nav.hubStoreSub', route: '/hub-store', color: '#8b5cf6', simpleOnly: true },
  { id: 'leaderboard', labelKey: 'nav.leaders', subtitleKey: 'nav.leadersSub', route: '/leaderboard', color: '#eab308' },
  { id: 'museum', labelKey: 'nav.museum', subtitleKey: 'nav.museumSub', route: '/Museum', color: '#a855f7' },
  { id: 'cashstore', labelKey: 'nav.cashShop', subtitleKey: 'nav.cashShopSub', route: '/cash-store', color: '#22c55e' },
];

// Format money for display
function formatMoney(amount) {
  if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(1)}B`;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount?.toLocaleString() || 0}`;
}

export function VerticalNav({ gameMode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { worldState } = useWorldState();

  const [eosCollapsed, setEosCollapsed] = useState(false);

  // Get cash and revenue data
  const cash = worldState?.airline?.cash || 0;
  const dailyRevenue = worldState?.stats?.dailyRevenue || 0;

  // Get fuel data
  const fuelReserves = worldState?.fuelReserves || 500000;
  const maxFuelCapacity = worldState?.maxFuelCapacity || 2000000;

  // Filter items based on game mode
  const visibleItems = NAV_ITEMS.filter(item => {
    if (item.simpleOnly) {
      return gameMode === 'simple' || gameMode === 'arcade';
    }
    return true;
  });

  // EOS Progress calculation
  const eosProgress = useMemo(() => {
    if (!worldState) return null;

    const eos = worldState.eos || {};
    const hasEOS = eos.hasEOS;
    const consecutiveDays = eos.consecutiveDays || 0;
    const requiredDays = EOS_REQUIREMENTS.QUALIFICATION_DAYS;
    const gameDay = worldState.dayNumber || 1;

    // Calculate individual requirements
    const repScore = worldState.reputation?.currentScore || 0;
    const tier = calculateTier(repScore);

    // OTP: Match OpsCenter calculation (all-time on-time percentage)
    const completedFlights = worldState.stats?.completedFlights || 0;
    const onTimeFlights = worldState.stats?.onTimeFlights || 0;
    const otp = completedFlights > 0 ? onTimeFlights / completedFlights : 1.0;

    return {
      hasEOS,
      days: consecutiveDays,
      requiredDays,
      gameDay,
      progress: hasEOS ? 100 : Math.round((consecutiveDays / requiredDays) * 100),
      checks: {
        tier: tier >= 5,
        otp: otp >= 0.85,
        // These are daily resets, shown as "today OK" if no issues
        cancellations: true, // Would need today's data
        safety: true,
        cashflow: true,
      },
      tier,
      otp: Math.round(otp * 100),
    };
  }, [worldState]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '12px 10px',
        backgroundColor: '#0c0c0c',
        borderRight: '1px solid #1a1a1a',
        height: '100%',
        width: '120px',
        flexShrink: 0,
      }}
    >
      {/* Money Display */}
      <div
        style={{
          padding: '10px 8px',
          backgroundColor: '#0d1f1a',
          border: '1px solid #22c55e40',
          borderRadius: '6px',
          marginBottom: '8px',
        }}
      >
        <div style={{ fontSize: '9px', color: '#22c55e80', fontWeight: '600', letterSpacing: '0.5px' }}>
          {t('nav.cash')}
        </div>
        <div style={{ fontSize: '16px', fontWeight: '700', color: '#22c55e', fontFamily: 'monospace' }}>
          {formatMoney(cash)}
        </div>
        {dailyRevenue > 0 && (
          <div style={{ fontSize: '10px', color: '#60ffd0', marginTop: '4px' }}>
            +{formatMoney(dailyRevenue)} {t('nav.today')}
          </div>
        )}
      </div>

      {/* Fuel Gauge */}
      <FuelGauge
        currentFuel={fuelReserves}
        maxFuel={maxFuelCapacity}
        dailyBurnRate={50000}
      />

      {/* Wings Balance */}
      {(() => {
        const wings = getWingsState(worldState);
        const progress = (wings.flightsSinceLastWing / WINGS_CONFIG.flightsPerWing) * 100;
        return (
          <div
            style={{
              padding: '8px',
              backgroundColor: '#1a0f2e',
              border: '1px solid #8b5cf640',
              borderRadius: '6px',
              marginBottom: '8px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '9px', color: '#8b5cf6', fontWeight: '600', letterSpacing: '0.5px' }}>
                {t('nav.wings')}
              </span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#a78bfa', fontFamily: 'monospace' }}>
                {wings.balance}
              </span>
            </div>
            {/* Progress to next Wing */}
            <div style={{ marginTop: '4px' }}>
              <div style={{
                height: '3px',
                backgroundColor: '#2d1f4e',
                borderRadius: '2px',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: '#8b5cf6',
                  borderRadius: '2px',
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <div style={{ fontSize: '8px', color: '#6b5090', marginTop: '2px', textAlign: 'right' }}>
                {wings.flightsSinceLastWing}/{WINGS_CONFIG.flightsPerWing}
              </div>
            </div>
          </div>
        );
      })()}

      {visibleItems.map((item) => {
        const isActive = location.pathname === item.route;
        const label = t(item.labelKey);
        const subtitle = t(item.subtitleKey);

        return (
          <button
            key={item.id}
            onClick={() => navigate(item.route)}
            title={subtitle}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              gap: '2px',
              padding: '10px 12px',
              backgroundColor: isActive ? '#1a1a1a' : 'transparent',
              border: isActive ? `1px solid ${item.color}50` : '1px solid transparent',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              borderLeft: isActive ? `3px solid ${item.color}` : '3px solid transparent',
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = '#151515';
                e.currentTarget.style.borderLeftColor = item.color + '60';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderLeftColor = 'transparent';
              }
            }}
          >
            <span
              style={{
                fontSize: '12px',
                fontWeight: '700',
                color: isActive ? item.color : '#a0a0a0',
                textAlign: 'left',
                lineHeight: 1.2,
                letterSpacing: '0.3px',
              }}
            >
              {label}
            </span>
            <span
              style={{
                fontSize: '9px',
                fontWeight: '400',
                color: isActive ? item.color + 'aa' : '#555',
                textAlign: 'left',
                lineHeight: 1.1,
              }}
            >
              {subtitle}
            </span>
          </button>
        );
      })}

      {/* EOS Progress Widget - Collapsible */}
      {eosProgress && (
        <div
          style={{
            position: 'fixed',
            bottom: '58px',
            left: '120px',
            backgroundColor: eosProgress.hasEOS ? '#0d2818' : '#0a0a0a',
            border: `2px solid ${eosProgress.hasEOS ? '#22c55e' : eosProgress.days > 0 ? '#f59e0b' : '#333'}`,
            borderRadius: '8px 8px 0 0',
            zIndex: 9999,
            width: '160px',
            boxShadow: eosProgress.days > 0
              ? '0 0 20px rgba(245, 158, 11, 0.3), 4px -4px 20px rgba(0, 0, 0, 0.8)'
              : '4px -4px 20px rgba(0, 0, 0, 0.8)',
            transition: 'height 0.2s ease',
          }}
        >
          {/* Collapse/Expand toggle bar */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              setEosCollapsed(prev => !prev);
            }}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '4px 0',
              cursor: 'pointer',
              borderBottom: eosCollapsed ? 'none' : `1px solid ${eosProgress.hasEOS ? '#22c55e30' : '#333'}`,
            }}
          >
            <span style={{
              fontSize: '10px',
              color: '#666',
              userSelect: 'none',
              transition: 'transform 0.2s ease',
              display: 'inline-block',
              transform: eosCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            }}>
              ▲
            </span>
          </div>

          {/* Collapsed: just show EOS label + day counter */}
          {eosCollapsed ? (
            <div
              onClick={() => navigate('/eos-store')}
              style={{ padding: '6px 12px', cursor: 'pointer', textAlign: 'center' }}
            >
              <span style={{
                fontSize: '10px',
                fontWeight: '800',
                color: eosProgress.hasEOS ? '#22c55e' : '#f59e0b',
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}>
                {eosProgress.hasEOS ? 'EOS' : `${eosProgress.days}/${eosProgress.requiredDays}`}
              </span>
            </div>
          ) : (
            /* Expanded: full EOS widget */
            <div
              onClick={() => navigate('/eos-store')}
              style={{ padding: '12px 16px', cursor: 'pointer' }}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}>
                <span style={{
                  fontSize: '10px',
                  fontWeight: '800',
                  color: eosProgress.hasEOS ? '#22c55e' : '#f59e0b',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                }}>
                  {eosProgress.hasEOS ? `✦ ${t('nav.eosActive')}` : t('nav.eos')}
                </span>
                <span style={{
                  fontSize: '9px',
                  fontWeight: '600',
                  color: '#666',
                }}>
                  {t('nav.day')} {eosProgress.gameDay}
                </span>
              </div>

              {!eosProgress.hasEOS && (
                <>
                  {/* Big day counter */}
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '800',
                    color: '#fff',
                    fontFamily: 'monospace',
                    lineHeight: 1,
                    marginBottom: '6px',
                  }}>
                    {eosProgress.days}<span style={{ color: '#555', fontSize: '18px' }}>/{eosProgress.requiredDays}</span>
                  </div>

                  {/* Progress label */}
                  <div style={{
                    fontSize: '9px',
                    color: '#888',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {t('nav.consecutiveDays')}
                  </div>

                  {/* Progress bar - bigger */}
                  <div style={{
                    height: '6px',
                    backgroundColor: '#333',
                    borderRadius: '3px',
                    marginBottom: '10px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${eosProgress.progress}%`,
                      backgroundColor: eosProgress.days > 10 ? '#22c55e' : '#f59e0b',
                      transition: 'width 0.3s ease',
                      boxShadow: eosProgress.days > 0 ? '0 0 8px rgba(245, 158, 11, 0.5)' : 'none',
                    }} />
                  </div>

                  {/* Requirements grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '4px',
                    fontSize: '9px',
                  }}>
                    <div style={{
                      padding: '4px 6px',
                      borderRadius: '4px',
                      backgroundColor: eosProgress.checks.tier ? '#22c55e15' : '#ef444415',
                      border: `1px solid ${eosProgress.checks.tier ? '#22c55e40' : '#ef444440'}`,
                      color: eosProgress.checks.tier ? '#22c55e' : '#ef4444',
                    }}>
                      <div style={{ fontWeight: '600' }}>Tier {eosProgress.tier}</div>
                      <div style={{ fontSize: '8px', opacity: 0.7 }}>{t('nav.repLevel')}</div>
                    </div>
                    <div style={{
                      padding: '4px 6px',
                      borderRadius: '4px',
                      backgroundColor: eosProgress.checks.otp ? '#22c55e15' : '#ef444415',
                      border: `1px solid ${eosProgress.checks.otp ? '#22c55e40' : '#ef444440'}`,
                      color: eosProgress.checks.otp ? '#22c55e' : '#ef4444',
                    }}>
                      <div style={{ fontWeight: '600' }}>{eosProgress.otp}%</div>
                      <div style={{ fontSize: '8px', opacity: 0.7 }}>{t('nav.otpRate')}</div>
                    </div>
                  </div>

                  {/* Hint text */}
                  <div style={{
                    marginTop: '8px',
                    fontSize: '8px',
                    color: '#555',
                    textAlign: 'center',
                  }}>
                    {t('nav.eosHint')}
                  </div>
                </>
              )}

              {eosProgress.hasEOS && (
                <div style={{
                  textAlign: 'center',
                  padding: '8px 0',
                }}>
                  <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: '600' }}>
                    {t('nav.goldA380Unlocked')}
                  </div>
                  <div style={{ fontSize: '9px', color: '#22c55e60', marginTop: '4px' }}>
                    {t('nav.visitEosStore')}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VerticalNav;
